import React, { useEffect, useRef } from "react";

import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

const SITE_PUBLIC_KEY =
  process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY ||
  "1x00000000000000000000AA";

const TURNSTILE_SCRIPT_WAIT_TIMEOUT_MS = 10000;
const TURNSTILE_SCRIPT_POLL_INTERVAL_MS = 100;

export interface TurnstileProps {
  siteKey?: string;
  onSuccess: (token: string) => void;
  onError?: (errorCode?: string) => void;
  onExpire?: () => void;
  onTimeout?: () => void;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Bump this (e.g. a counter) to get a fresh token from the already-
   * rendered widget, e.g. after a form submission fails and the token that
   * was used is no longer good for a retry. Ignored on initial mount.
   */
  resetSignal?: number;
}

/**
 * The Turnstile script is loaded elsewhere (via next/script), so this just
 * waits for `window.turnstile` to show up rather than injecting a script
 * tag itself.
 */
function waitForTurnstileScript(
  window: NonNullable<ReturnType<typeof getWindow>>
): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.turnstile) {
      resolve(true);
      return;
    }
    logger.error({
      message: "window.turnstile is not defined yet, waiting for it to load",
    });
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += TURNSTILE_SCRIPT_POLL_INTERVAL_MS;
      if (window.turnstile) {
        clearInterval(interval);
        resolve(true);
      } else if (elapsed >= TURNSTILE_SCRIPT_WAIT_TIMEOUT_MS) {
        clearInterval(interval);
        logger.error({
          message: `window.turnstile was not defined after waiting ${TURNSTILE_SCRIPT_WAIT_TIMEOUT_MS}ms`,
        });
        resolve(false);
      }
    }, TURNSTILE_SCRIPT_POLL_INTERVAL_MS);
  });
}

export function Turnstile({
  siteKey = SITE_PUBLIC_KEY,
  onSuccess,
  onError,
  onExpire,
  onTimeout,
  className,
  style,
  resetSignal,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const isFirstResetSignalRef = useRef(true);

  useEffect(() => {
    const window = getWindow();
    const container = containerRef.current;
    if (!window || !container) {
      return;
    }

    // Guard against rendering a second widget into a container that already
    // has one, which is what causes Cloudflare's "already been rendered"
    // error.
    if (container.querySelector("iframe")) {
      return;
    }

    // React 18 Strict Mode invokes this effect twice in a row on mount
    // (mount -> cleanup -> mount) before either render() call has actually
    // fired, since it's deferred behind the async script wait below. Use a
    // flag scoped to *this* effect invocation (not a ref shared across
    // invocations) so the first invocation's cleanup can cancel only its
    // own pending render.
    let cancelled = false;

    waitForTurnstileScript(window).then((isReady) => {
      if (!isReady || cancelled) {
        return;
      }
      // Re-check right before rendering: this invocation may have lost the
      // race to a later one that already rendered into the same container
      // while we were waiting.
      if (container.querySelector("iframe")) {
        return;
      }
      // Note: turnstile.ready() is not supported when the script is loaded
      // with async/defer (as it is here, via next/script), so we render
      // directly once window.turnstile is confirmed to exist.
      const render = window.turnstile?.render;
      if (typeof render !== "function") {
        logger.error({
          message: "Turnstile render property is not a function",
          data: { render },
        });
        return;
      }
      try {
        widgetIdRef.current = render(container, {
          sitekey: siteKey,
          "refresh-expired": "auto",
          "response-field-name": "cfTurnstileResponse",
          retry: "auto",
          theme: "light",
          size: "flexible",
          callback: onSuccess,
          "error-callback": (errorCode) => {
            logger.error({
              message: "Turnstile error-callback fired",
              data: { errorCode },
            });
            onError?.(errorCode);
          },
          "expired-callback": () => {
            logger.error({ message: "Turnstile expired-callback fired" });
            onExpire?.();
          },
          "timeout-callback": () => {
            logger.error({ message: "Turnstile timeout-callback fired" });
            onTimeout?.();
          },
        });
      } catch (error) {
        logger.error({ message: "Error when rendering Turnstile", error });
      }
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current) {
        try {
          getWindow()?.turnstile?.remove(widgetIdRef.current);
        } catch (error) {
          logger.error({ message: "Error when removing Turnstile", error });
        }
        widgetIdRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstResetSignalRef.current) {
      isFirstResetSignalRef.current = false;
      return;
    }
    if (!widgetIdRef.current) {
      return;
    }
    try {
      getWindow()?.turnstile?.reset(widgetIdRef.current);
    } catch (error) {
      logger.error({ message: "Error when resetting Turnstile", error });
    }
  }, [resetSignal]);

  return <div ref={containerRef} className={className} style={style} />;
}
